import React, { useState, useEffect } from 'react';
import RecipeConverter from './components/RecipeConverter';
import HelpGuide from './components/HelpGuide';
import AdminPanel from './components/AdminPanel';
import { authAPI, getCurrentUser, setCurrentUser, setAuthToken, aiAPI, setLiveMcSession } from './services/api';
import { initMcNative, isNativeApp, connectMcNative, getInstalledAppVersion } from './services/mcNative';
import { useLang } from './i18n/LangContext';
import './styles.css';

const LANGS = [
  { code: 'pl', label: 'PL' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
];

function App() {
  const { t, tErr, lang, changeLang } = useLang();
  const [user, setUser] = useState(getCurrentUser());
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [mcSession, setMcSession] = useState(null);
  const [extDetected, setExtDetected] = useState(false);
  const [showHelpGuide, setShowHelpGuide] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  // Przepis innego użytkownika wczytany przez admina ("Wczytaj do konwertera")
  const [adminRecipe, setAdminRecipe] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPass, setResetNewPass] = useState('');
  const [resetConfirmPass, setResetConfirmPass] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [contactTab, setContactTab] = useState('contact');
  const [contactForm, setContactForm] = useState({ topic: '', email: '', message: '' });
  const [contactSent, setContactSent] = useState(false);
  const [contactSending, setContactSending] = useState(false);
  const [contactError, setContactError] = useState('');
  // „Jak zacząć?" pulsuje dla nowych userów, dopóki nie połączą MC ani nie otworzą przewodnika
  const [helpSeen, setHelpSeen] = useState(() => localStorage.getItem('mm_help_seen') === '1');
  const openHelpGuide = () => {
    setShowHelpGuide(true);
    setHelpSeen(true);
    localStorage.setItem('mm_help_seen', '1');
  };
  // Usuwanie konta
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const closeAccountModal = () => {
    setShowAccountModal(false);
    setDeleteConfirm(false);
    setDeletePassword('');
    setDeleteError('');
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await authAPI.deleteAccount(deletePassword);
      authAPI.logout();
      setUser(null);
      setMcSession(null);
      setLiveMcSession(null);
      closeAccountModal();
      setAuthSuccess(t('account.deleted'));
      setTimeout(() => setAuthSuccess(''), 5000);
    } catch (error) {
      setDeleteError(tErr(error.response?.data?.error) || t('account.delete_error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !user) {
      authAPI.profile()
        .then(r => { setCurrentUser(r.data.user); setUser(r.data.user); })
        .catch(() => { localStorage.removeItem('token'); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Baner „nowa wersja apki” — tylko w apce Android, gdy /android/version.json
  // ma wyższy versionCode niż zainstalowany APK. Zamknięcie pamiętamy per wersja.
  const [appUpdate, setAppUpdate] = useState(null);
  useEffect(() => {
    if (!isNativeApp()) return;
    let cancelled = false;
    Promise.all([
      getInstalledAppVersion(),
      fetch('/android/version.json', { cache: 'no-store' }).then(r => (r.ok ? r.json() : null)),
    ]).then(([installed, latest]) => {
      if (cancelled || !latest || !installed || latest.versionCode <= installed) return;
      if (localStorage.getItem('mm_app_update_dismissed') === String(latest.versionCode)) return;
      setAppUpdate(latest);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  function dismissAppUpdate() {
    localStorage.setItem('mm_app_update_dismissed', String(appUpdate.versionCode));
    setAppUpdate(null);
  }

  useEffect(() => {
    // W apce Android moduł natywny odpowiada na te same pingi co wtyczka —
    // rejestrujemy go przed startem handshake'u, reszta przepływu bez zmian.
    initMcNative();
    let lastSessionJson = null;
    function applySession(s) {
      if (!s || !s.wp_logged_in_value || !s.lidl_sso_token) return;
      const json = JSON.stringify(s);
      if (json === lastSessionJson) return; // ta sama sesja — bez ponownego zapisu do DB
      lastSessionJson = json;
      setMcSession(s);
      // Nagłówek X-MC-Session ma pierwszeństwo przed kopią w bazie — dzięki temu
      // backend rozmawia z MC na sesji sprzed sekund, a nie sprzed godzin
      setLiveMcSession(s);
      const parts = [];
      if (s.gclb) parts.push(`GCLB=${s.gclb}`);
      if (s.php_session_id) parts.push(`PHPSESSID=${s.php_session_id}`);
      parts.push(`wp-wpml_current_language=pl`);
      parts.push(`${s.wp_logged_in_key}=${s.wp_logged_in_value}`);
      parts.push(`lidl_sso_id_token=${s.lidl_sso_token}`);
      aiAPI.saveMCSession(parts.join('; ')).catch(() => {});
    }
    const ping = () => document.dispatchEvent(new CustomEvent('MC_EXT_GET_SESSION'));
    // Rozszerzenie/apka natywna odpowiada na KAŻDY ping (nawet bez sesji MC) —
    // pierwsza odpowiedź dowolnej treści dowodzi, że kanał żyje (extDetected).
    const handler = (e) => { setExtDetected(true); applySession(e.detail?.session); };
    document.addEventListener('MC_EXT_SESSION_RESULT', handler);
    // Handshake z wtyczką to wyścig w obie strony: jej content script może
    // wstrzyknąć się po nas (pojedynczy ping przepada), albo ogłosić sesję
    // zanim zarejestrujemy listener. Ponawiamy ping z rosnącym odstępem,
    // aż sesja dotrze — nasłuch po stronie wtyczki jest trwały.
    const timers = [500, 1500, 3000, 6000, 12000, 25000].map(d =>
      setTimeout(() => { if (!lastSessionJson) ping(); }, d)
    );
    // Powrót do karty = szansa na świeżą sesję (MC wygasza ją po ~5 min)
    const onVisible = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      timers.forEach(clearTimeout);
      document.removeEventListener('MC_EXT_SESSION_RESULT', handler);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const response = await authAPI.login(loginData);
      const { access_token, user } = response.data;
      setAuthToken(access_token);
      setCurrentUser(user);
      setUser(user);
      setShowLoginModal(false);
      setLoginData({ username: '', password: '' });
      setAuthSuccess(t('login.success'));
      setTimeout(() => setAuthSuccess(''), 3000);
    } catch (error) {
      setAuthError(tErr(error.response?.data?.error) || t('login.error'));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (registerData.password !== registerData.confirmPassword) {
      setAuthError(t('register.passwords_mismatch'));
      return;
    }
    try {
      await authAPI.register({
        username: registerData.name,
        email: registerData.email,
        password: registerData.password,
      });
      setAuthSuccess(t('register.success'));
      setRegisterData({ name: '', email: '', password: '', confirmPassword: '' });
      setTimeout(() => {
        setShowRegisterModal(false);
        setShowLoginModal(true);
        setAuthSuccess('');
      }, 2000);
    } catch (error) {
      setAuthError(tErr(error.response?.data?.error) || t('register.error'));
    }
  };

  const openResetModal = () => {
    setResetStep(1); setResetEmail(''); setResetCode('');
    setResetNewPass(''); setResetConfirmPass('');
    setResetError('');
    setShowLoginModal(false);
    setShowResetModal(true);
  };

  const handleResetStep1 = async (e) => {
    e.preventDefault();
    setResetError(''); setResetLoading(true);
    try {
      await authAPI.forgotPassword(resetEmail);
      setResetStep(2);
    } catch (err) {
      setResetError(tErr(err.response?.data?.error) || t('reset.error_email'));
    } finally { setResetLoading(false); }
  };

  const handleResetStep2 = async (e) => {
    e.preventDefault();
    setResetError('');
    if (resetNewPass !== resetConfirmPass) { setResetError(t('reset.passwords_mismatch')); return; }
    if (resetNewPass.length < 6) { setResetError(t('reset.too_short')); return; }
    setResetLoading(true);
    try {
      const r = await authAPI.verifyResetCode(resetEmail, resetCode, resetNewPass);
      const { access_token, user } = r.data;
      setAuthToken(access_token);
      setCurrentUser(user);
      setUser(user);
      setShowResetModal(false);
      setAuthSuccess(t('reset.success'));
      setTimeout(() => setAuthSuccess(''), 4000);
    } catch (err) {
      setResetError(tErr(err.response?.data?.error) || t('reset.error_code'));
    } finally { setResetLoading(false); }
  };

  const handleLogout = () => {
    authAPI.logout();
    setUser(null);
    setMcSession(null);
    setLiveMcSession(null);
    setAuthSuccess(t('header.logout_success'));
    setTimeout(() => setAuthSuccess(''), 3000);
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="app">
      {showHelpGuide && <HelpGuide onClose={() => setShowHelpGuide(false)} />}
      {showAdminPanel && user?.is_admin && (
        <AdminPanel currentUserId={user.id} onClose={() => setShowAdminPanel(false)}
          onLoadRecipe={r => { setAdminRecipe({ ...r, _loadedAt: Date.now() }); setShowAdminPanel(false); }} />
      )}

      {/* Login modal */}
      {showLoginModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2><i className="fas fa-sign-in-alt"></i> {t('login.title')}</h2>
              <button className="modal-close" onClick={() => setShowLoginModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleLogin}>
              <div className="modal-body">
                {authError && <div className="auth-error">{authError}</div>}
                <div className="form-group">
                  <label htmlFor="username">{t('login.username')}</label>
                  <input type="email" id="username" value={loginData.username}
                    onChange={(e) => setLoginData({...loginData, username: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label htmlFor="password">{t('login.password')}</label>
                  <input type="password" id="password" value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})} required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn-primary">{t('login.submit')}</button>
                <button type="button" className="btn-secondary" onClick={() => setShowLoginModal(false)}>
                  {t('cancel')}
                </button>
              </div>
            </form>
            <div className="modal-switch">
              <p>{t('login.no_account')} <button onClick={() => { setShowLoginModal(false); setShowRegisterModal(true); }}>{t('login.register_link')}</button></p>
              <p><button className="modal-link-subtle" onClick={openResetModal}>{t('login.forgot')}</button></p>
            </div>
          </div>
        </div>
      )}

      {/* Register modal */}
      {showRegisterModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2><i className="fas fa-user-plus"></i> {t('register.title')}</h2>
              <button className="modal-close" onClick={() => setShowRegisterModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleRegister}>
              <div className="modal-body">
                {authError && <div className="auth-error">{authError}</div>}
                {authSuccess && <div className="auth-success">{authSuccess}</div>}
                <div className="form-group"><label>{t('register.name')}</label><input type="text" value={registerData.name} onChange={e=>setRegisterData({...registerData,name:e.target.value})} required /></div>
                <div className="form-group"><label>{t('register.email')}</label><input type="email" value={registerData.email} onChange={e=>setRegisterData({...registerData,email:e.target.value})} required /></div>
                <div className="form-group"><label>{t('register.password')}</label><input type="password" value={registerData.password} onChange={e=>setRegisterData({...registerData,password:e.target.value})} required /></div>
                <div className="form-group"><label>{t('register.confirm_password')}</label><input type="password" value={registerData.confirmPassword} onChange={e=>setRegisterData({...registerData,confirmPassword:e.target.value})} required /></div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn-primary">{t('register.submit')}</button>
                <button type="button" className="btn-secondary" onClick={() => setShowRegisterModal(false)}>{t('cancel')}</button>
              </div>
            </form>
            <div className="modal-switch">
              <p>{t('register.has_account')} <button onClick={() => { setShowRegisterModal(false); setShowLoginModal(true); }}>{t('register.login_link')}</button></p>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {showResetModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2><i className="fas fa-key"></i> {t('reset.title')}</h2>
              <button className="modal-close" onClick={() => setShowResetModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            {resetStep === 1 ? (
              <form onSubmit={handleResetStep1}>
                <div className="modal-body">
                  {resetError && <div className="auth-error">{resetError}</div>}
                  <p className="reset-intro">{t('reset.intro')}</p>
                  <div className="form-group">
                    <label>{t('reset.email_label')}</label>
                    <input type="email" value={resetEmail}
                      onChange={e => setResetEmail(e.target.value)}
                      placeholder={t('reset.email_placeholder')} required autoFocus />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="submit" className="btn-primary" disabled={resetLoading}>
                    {resetLoading ? <><i className="fas fa-spinner fa-spin"></i> {t('reset.sending')}</> : <><i className="fas fa-paper-plane"></i> {t('reset.send_code')}</>}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setShowResetModal(false)}>{t('cancel')}</button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetStep2}>
                <div className="modal-body">
                  {resetError && <div className="auth-error">{resetError}</div>}
                  <div className="reset-sent-info">
                    <i className="fas fa-envelope-open-text"></i>
                    <span>{t('reset.code_sent')} <strong>{resetEmail}</strong></span>
                  </div>
                  <div className="form-group">
                    <label>{t('reset.code_label')}</label>
                    <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                      value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456" required autoFocus className="reset-code-input" />
                  </div>
                  <div className="form-group">
                    <label>{t('reset.new_password')}</label>
                    <input type="password" value={resetNewPass}
                      onChange={e => setResetNewPass(e.target.value)}
                      placeholder={t('reset.min_chars')} required minLength={6} />
                  </div>
                  <div className="form-group">
                    <label>{t('reset.confirm_password')}</label>
                    <input type="password" value={resetConfirmPass}
                      onChange={e => setResetConfirmPass(e.target.value)}
                      placeholder={t('reset.confirm_placeholder')} required />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="submit" className="btn-primary" disabled={resetLoading}>
                    {resetLoading ? <><i className="fas fa-spinner fa-spin"></i> {t('reset.changing')}</> : <><i className="fas fa-check"></i> {t('reset.change')}</>}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setResetStep(1)}>
                    <i className="fas fa-arrow-left"></i> {t('back')}
                  </button>
                </div>
                <div className="modal-switch">
                  <p><button type="button" className="modal-link-subtle" onClick={handleResetStep1} disabled={resetLoading}>
                    {t('reset.resend')}
                  </button></p>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Account modal */}
      {showAccountModal && user && (
        <div className="modal-overlay" onClick={closeAccountModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-user-cog"></i> {t('account.title')}</h2>
              <button className="modal-close" onClick={closeAccountModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="account-info-row">
                <span className="account-info-label">{t('account.username')}</span>
                <span className="account-info-value">{user.username}</span>
              </div>
              {user.email && (
                <div className="account-info-row">
                  <span className="account-info-label">{t('account.email')}</span>
                  <span className="account-info-value">{user.email}</span>
                </div>
              )}

              <div className="account-danger">
                <h3><i className="fas fa-triangle-exclamation"></i> {t('account.danger_title')}</h3>
                {!deleteConfirm ? (
                  <>
                    <p className="account-danger-desc">{t('account.delete_desc')}</p>
                    <button type="button" className="btn-danger-outline" onClick={() => setDeleteConfirm(true)}>
                      <i className="fas fa-trash"></i> {t('account.delete_btn')}
                    </button>
                  </>
                ) : (
                  <form onSubmit={handleDeleteAccount}>
                    <p className="account-danger-warn">{t('account.delete_warn')}</p>
                    {deleteError && <div className="auth-error">{deleteError}</div>}
                    <div className="form-group">
                      <label htmlFor="delete-password">{t('account.delete_password_label')}</label>
                      <input type="password" id="delete-password" value={deletePassword}
                        onChange={e => setDeletePassword(e.target.value)} required autoFocus />
                    </div>
                    <div className="account-danger-actions">
                      <button type="submit" className="btn-danger" disabled={deleteLoading || !deletePassword}>
                        {deleteLoading
                          ? <><i className="fas fa-spinner fa-spin"></i> {t('account.deleting')}</>
                          : <><i className="fas fa-trash"></i> {t('account.delete_confirm_btn')}</>}
                      </button>
                      <button type="button" className="btn-secondary"
                        onClick={() => { setDeleteConfirm(false); setDeletePassword(''); setDeleteError(''); }}>
                        {t('cancel')}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="header">
        <div className="logo">
          <span className="logo-text">
            <span className="logo-mate">Mate</span><span className="logo-meal">Meal</span>
          </span>
        </div>
        <div className="user-section">
          {/* Theme toggle */}
          <button className="btn-theme" onClick={toggleTheme} title={theme === 'dark' ? 'Tryb jasny' : 'Tryb ciemny'}>
            <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`}></i>
          </button>

          {/* Language switcher */}
          <div className="lang-switcher">
            {LANGS.map(l => (
              <button
                key={l.code}
                className={`lang-btn ${lang === l.code ? 'lang-btn--active' : ''}`}
                onClick={() => changeLang(l.code)}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button
            className={`btn-help ${user && !mcSession && !helpSeen ? 'btn-help--attn' : ''}`}
            onClick={openHelpGuide}
            title={t('header.help')}
          >
            <i className="fas fa-question-circle"></i>
            <span>{t('header.help')}</span>
          </button>
          {user ? (
            <div className="user-info">
              {user.is_admin && (
                <button className="btn-admin" onClick={() => setShowAdminPanel(true)} title={t('header.admin')}>
                  <i className="fas fa-shield-alt"></i>
                  <span>{t('header.admin')}</span>
                </button>
              )}
              <span className="user-name">{user.username}</span>
              <button className="btn-account" onClick={() => setShowAccountModal(true)} title={t('account.title')}>
                <i className="fas fa-user-cog"></i>
              </button>
              <button className="btn-logout" onClick={handleLogout} title={t('account.logout')}>
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          ) : (
            <div className="auth-buttons">
              <button className="btn-login" onClick={() => setShowLoginModal(true)}>
                {t('header.login')}
              </button>
              <button className="btn-register" onClick={() => setShowRegisterModal(true)}>
                {t('header.register')}
              </button>
            </div>
          )}
        </div>
      </header>

      {appUpdate && (
        <div className="ext-store-banner">
          <i className="fas fa-mobile-alt ext-store-banner__icon"></i>
          <span className="ext-store-banner__text">{t('app_update.text').replace('{v}', appUpdate.versionName)}</span>
          {/* Domena bez www to dla Capacitora obcy host → otwiera się w przeglądarce
              systemowej. WebView apki nie umie pobierać plików, więc APK musi
              ściągnąć Chrome. */}
          <a className="btn btn-primary ext-store-banner__cta" href="https://matemeal.com/android/">
            {t('app_update.cta')}
          </a>
          <button className="ext-store-banner__close" onClick={dismissAppUpdate} aria-label={t('close')}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <main className="main-content">
        {authError && (
          <div className="global-error">
            <i className="fas fa-exclamation-triangle"></i> {authError}
            <button className="close-error" onClick={() => setAuthError('')}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
        {authSuccess && (
          <div className="global-success">
            <i className="fas fa-check-circle"></i> {authSuccess}
            <button className="close-success" onClick={() => setAuthSuccess('')}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        <div className="content-area">
          {!user ? (
            <div className="hero">
              <div className="hero-icon">
                <i className="fas fa-robot"></i>
                <i className="fas fa-arrow-right hero-arrow"></i>
                <i className="fas fa-blender"></i>
              </div>
              <h1>{t('hero.title')}</h1>
              <p className="hero-sub">{t('hero.sub')}</p>
              <div className="hero-features">
                <div className="hero-feat"><i className="fas fa-paste"></i>{t('hero.feat1')}</div>
                <div className="hero-feat"><i className="fas fa-magic"></i>{t('hero.feat2')}</div>
                <div className="hero-feat"><i className="fas fa-paper-plane"></i>{t('hero.feat3')}</div>
              </div>
              <div className="hero-actions">
                <button className="btn-primary btn-lg" onClick={() => setShowLoginModal(true)}>
                  <i className="fas fa-sign-in-alt"></i> {t('hero.cta_login')}
                </button>
                <button className="btn-secondary btn-lg" onClick={openHelpGuide}>
                  <i className="fas fa-question-circle"></i> {t('hero.cta_help')}
                </button>
              </div>
            </div>
          ) : (
            <RecipeConverter mcSession={mcSession} extDetected={extDetected} user={user} externalRecipe={adminRecipe}
              onUserUpdate={u => { setCurrentUser(u); setUser(u); }}
              onMcConnect={isNativeApp() ? () => connectMcNative(lang, t('conv.mc_connect_hint')) : null}
              onMcSessionExpired={() => { setMcSession(null); setLiveMcSession(null); }} />
          )}
        </div>
      </main>

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <span className="footer-logo">
                <span className="logo-mate">Mate</span><span className="logo-meal">Meal</span>
              </span>
              <p className="footer-tagline">{t('footer.tagline')}</p>
            </div>
            <div className="footer-nav">
              <button className="footer-link" onClick={openHelpGuide}>
                <i className="fas fa-question-circle"></i> {t('footer.help')}
              </button>
              <button className="footer-link" onClick={() => { setContactTab('contact'); setContactForm({ topic: '', email: '', message: '' }); setContactSent(false); setShowContactModal(true); }}>
                <i className="fas fa-envelope"></i> {t('footer.contact')}
              </button>
              {!isNativeApp() && (
                <a className="footer-link" href="/android/">
                  <i className="fab fa-android"></i> {t('footer.android')}
                </a>
              )}
              <a className="footer-link" href="/privacy" target="_blank" rel="noreferrer">
                <i className="fas fa-shield-alt"></i> {t('footer.privacy')}
              </a>
              <a className="footer-link" href="https://www.facebook.com/profile.php?id=61590254290685" target="_blank" rel="noreferrer">
                <i className="fab fa-facebook"></i> Facebook
              </a>
            </div>
          </div>
          <div className="footer-disclaimers">
            <div className="footer-disc">
              <i className="fas fa-shield-alt"></i>
              <span><strong>{t('footer.security')}</strong> {t('footer.security_text')}</span>
            </div>
            <div className="footer-disc">
              <i className="fas fa-tag"></i>
              <span><strong>{t('footer.affiliate')}</strong> {t('footer.affiliate_text')}</span>
            </div>
          </div>
          <div className="footer-copy">
            &copy; {new Date().getFullYear()} MateMeal. {t('footer.rights')}
          </div>
        </div>
      </footer>

      {/* Contact modal */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => { setShowContactModal(false); setContactError(''); }}>
          <div className="modal contact-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="contact-tabs">
                <button className={`contact-tab ${contactTab === 'contact' ? 'active' : ''}`}
                  onClick={() => { setContactTab('contact'); setContactForm(p => ({ ...p, topic: '' })); setContactSent(false); }}>
                  <i className="fas fa-envelope"></i> {t('contact.tab_contact')}
                </button>
                <button className={`contact-tab ${contactTab === 'bug' ? 'active' : ''}`}
                  onClick={() => { setContactTab('bug'); setContactForm(p => ({ ...p, topic: t('contact.bug_topic') })); setContactSent(false); }}>
                  <i className="fas fa-bug"></i> {t('contact.tab_bug')}
                </button>
              </div>
              <button className="modal-close-btn" onClick={() => { setShowContactModal(false); setContactError(''); }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {contactSent ? (
                <div className="contact-sent">
                  <i className="fas fa-check-circle"></i>
                  <h3>{t('contact.sent_title')}</h3>
                  <p>{t('contact.sent_text')}</p>
                  <button className="btn-primary" onClick={() => { setShowContactModal(false); setContactError(''); }}>{t('close')}</button>
                </div>
              ) : (
                <form onSubmit={async e => {
                  e.preventDefault();
                  setContactSending(true);
                  setContactError('');
                  const subjectType = contactForm.topic || (contactTab === 'bug' ? t('contact.bug_topic') : t('contact.generic_topic'));
                  try {
                    const res = await fetch('/api/contact', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ subject_type: subjectType, message: contactForm.message, reply_email: contactForm.email }),
                    });
                    if (res.status === 429) { setContactError(t('contact.form.rate_limit')); return; }
                    if (!res.ok) throw new Error();
                    setContactSent(true);
                  } catch {
                    setContactError(t('contact.form.error_short'));
                  } finally {
                    setContactSending(false);
                  }
                }}>
                  <p className="contact-intro">
                    {contactTab === 'bug' ? t('contact.intro_bug') : t('contact.intro_contact')}
                  </p>
                  <div className="form-group">
                    <label>{t('contact.topic')}</label>
                    <input type="text" value={contactForm.topic} onChange={e => setContactForm(p => ({ ...p, topic: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>{t('contact.email')} <span className="optional">{t('contact.email_optional')}</span></label>
                    <input type="email" value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} placeholder={t('reset.email_placeholder')} />
                  </div>
                  <div className="form-group">
                    <label>{t('contact.message')}</label>
                    <textarea value={contactForm.message} onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))} rows={5} required />
                  </div>
                  {contactError && <p style={{color:'#f87171',fontSize:'0.83rem',margin:'0 0 8px'}}>{contactError}</p>}
                  <div className="modal-footer">
                    <button type="submit" className="btn-primary" disabled={contactSending}>
                      <i className="fas fa-paper-plane"></i> {contactSending ? t('contact.form.sending') : t('contact.send')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
